import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X, Cookie } from "lucide-react";

const STORAGE_KEY = "vdz_cookie_consent_v1";
const RESET_EVENT = "vdz:cookie-reset";

/**
 * Cookie consent state shape:
 * { necessary: true (always), preferences: boolean, decidedAt: ISO }
 * Absent key => banner shown.
 */
export function getConsent() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

/** Reopens the cookie banner. Call from a "Setări cookies" button. */
export function openCookieSettings() {
  try { localStorage.removeItem(STORAGE_KEY); } catch {}
  window.dispatchEvent(new Event(RESET_EVENT));
}

export default function CookieBanner() {
  const [consent, setConsent] = useState(() => getConsent());
  const [customizeOpen, setCustomizeOpen] = useState(false);
  const [prefs, setPrefs] = useState({ preferences: true });

  useEffect(() => {
    const onReset = () => { setConsent(null); setPrefs({ preferences: true }); setCustomizeOpen(false); };
    window.addEventListener(RESET_EVENT, onReset);
    return () => window.removeEventListener(RESET_EVENT, onReset);
  }, []);

  // Hide banner if consent already stored
  if (consent) return null;

  const save = (c) => {
    const full = { ...c, necessary: true, decidedAt: new Date().toISOString() };
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(full)); } catch {}
    setConsent(full);
    setCustomizeOpen(false);
  };

  const acceptAll = () => save({ preferences: true });
  const onlyNecessary = () => save({ preferences: false });
  const openCustomize = () => setCustomizeOpen(true);
  const savePrefs = () => save(prefs);

  return (
    <>
      {/* Banner */}
      <div
        role="dialog"
        aria-labelledby="cookie-banner-title"
        className="fixed left-4 right-4 bottom-4 md:left-6 md:right-6 md:bottom-6 z-[60]"
        data-testid="cookie-banner"
      >
        <div
          className="relative mx-auto max-w-5xl shadow-[0_30px_80px_-20px_rgba(11,19,43,0.4)]"
          style={{ background: "var(--vdz-navy)", color: "var(--vdz-cream)", border: "1px solid rgba(201,162,74,0.35)" }}
        >
          <div className="p-6 md:p-8 flex flex-col md:flex-row md:items-center gap-6">
            <div className="shrink-0 w-12 h-12 flex items-center justify-center gold-frame" style={{ background: "rgba(201,162,74,0.1)" }}>
              <Cookie size={20} style={{ color: "var(--vdz-gold)" }} />
            </div>
            <div className="flex-1">
              <div id="cookie-banner-title" className="font-serif-display text-xl md:text-2xl mb-1" style={{ color: "var(--vdz-cream)" }}>
                O pauză pentru cookie-uri ✨
              </div>
              <p className="text-sm leading-relaxed opacity-80">
                Folosim doar cookie-uri strict necesare pentru funcționarea coșului de cumpărături și, dacă ești de acord, câteva pentru a-ți reține preferințele.
                Niciodată pentru reclame sau tracking. Detalii în{" "}
                <Link to="/cookies" className="underline hover:text-[color:var(--vdz-gold-pale)]" data-testid="cookie-banner-policy-link">Politica de cookies</Link>.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 shrink-0">
              <button onClick={onlyNecessary} data-testid="cookie-banner-reject" className="btn-ghost text-xs px-5 py-3 whitespace-nowrap">
                Doar necesare
              </button>
              <button onClick={openCustomize} data-testid="cookie-banner-customize" className="btn-ghost text-xs px-5 py-3 whitespace-nowrap">
                Personalizează
              </button>
              <button onClick={acceptAll} data-testid="cookie-banner-accept" className="btn-gold text-xs px-5 py-3 whitespace-nowrap">
                Acceptă toate
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Customize Modal */}
      {customizeOpen && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" style={{ background: "rgba(11,19,43,0.75)", backdropFilter: "blur(4px)" }} onClick={() => setCustomizeOpen(false)} data-testid="cookie-customize-overlay">
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-lg"
            style={{ background: "var(--vdz-cream)", border: "1px solid var(--vdz-cream-border)" }}
            data-testid="cookie-customize-modal"
          >
            <div className="flex items-center justify-between p-6 border-b" style={{ borderColor: "var(--vdz-cream-border)" }}>
              <div>
                <div className="eyebrow">Preferințe cookies</div>
                <h3 className="font-serif-display text-2xl mt-1" style={{ color: "var(--vdz-navy)" }}>Alege ce preferi</h3>
              </div>
              <button onClick={() => setCustomizeOpen(false)} aria-label="Închide" className="p-2 hover:text-[color:var(--vdz-gold)]" data-testid="cookie-customize-close">
                <X size={20} />
              </button>
            </div>

            <div className="p-6 space-y-5">
              {/* Necessary - always on */}
              <div className="p-5 border" style={{ borderColor: "var(--vdz-cream-border)", background: "white" }}>
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="font-serif-display text-lg" style={{ color: "var(--vdz-navy)" }}>Strict necesare</div>
                    <p className="text-sm opacity-70 mt-1 leading-relaxed">
                      Esențiale pentru coșul de cumpărături, sesiunea admin și pentru ca site-ul să funcționeze. Nu pot fi dezactivate.
                    </p>
                  </div>
                  <div className="shrink-0 px-3 py-1 text-[10px] uppercase tracking-[0.18em] font-serif-accent" style={{ background: "var(--vdz-gold)", color: "var(--vdz-navy)" }}>
                    Activat
                  </div>
                </div>
              </div>

              {/* Preferences - toggle */}
              <div className="p-5 border" style={{ borderColor: "var(--vdz-cream-border)", background: "white" }}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="font-serif-display text-lg" style={{ color: "var(--vdz-navy)" }}>Preferințe</div>
                    <p className="text-sm opacity-70 mt-1 leading-relaxed">
                      Rețin alegerile tale (ex. filtrele din magazin, modul de vizualizare) ca site-ul să fie mai confortabil pentru tine la vizitele următoare.
                    </p>
                  </div>
                  <button
                    role="switch"
                    aria-checked={prefs.preferences}
                    onClick={() => setPrefs((p) => ({ ...p, preferences: !p.preferences }))}
                    data-testid="cookie-pref-preferences-toggle"
                    className="shrink-0 relative w-12 h-6 transition-colors"
                    style={{ background: prefs.preferences ? "var(--vdz-gold)" : "#d4ccbb" }}
                  >
                    <span
                      className="absolute top-0.5 w-5 h-5 bg-white transition-all"
                      style={{ left: prefs.preferences ? "26px" : "2px" }}
                    />
                  </button>
                </div>
              </div>

              <p className="text-xs opacity-60 leading-relaxed">
                Momentan VIS DE ZI nu folosește cookie-uri de marketing, analytics terți sau remarketing.
                Dacă vreodată vom adăuga astfel de cookie-uri, îți vom cere acordul explicit.
              </p>
            </div>

            <div className="p-6 border-t flex gap-3 justify-end" style={{ borderColor: "var(--vdz-cream-border)" }}>
              <button onClick={onlyNecessary} className="btn-dark text-xs" data-testid="cookie-customize-reject">
                Refuză tot
              </button>
              <button onClick={savePrefs} className="btn-gold text-xs" data-testid="cookie-customize-save">
                Salvează preferințele
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
